import React, { useState, useEffect, useRef } from 'react';
import { Settings, Info, AlertCircle, Check, Lock } from 'lucide-react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { useDealCommissionStore } from '../../lib/stores/dealCommissionStore';
import { useReferrerStore } from '../../lib/stores/referrerStore';
import { useUserStore } from '../../lib/stores/userStore';
import { useServiceCommissionStore } from '../../lib/stores/serviceCommissionStore';
import { supabase } from '../../lib/supabase';
import { hasPersonCommissionPayments } from '../../lib/utils/commissionPaymentCheck';
import type {
  DealCommissionOverrideFormData,
  EffectiveCommissionRate,
  CommissionHierarchyDisplay
} from '../../lib/types';
import { formatReferrerName } from '../../lib/utils/referrerDisplayName';

interface DealCommissionSectionProps {
  dealId?: string;
  clientId?: string;
  assignedTo?: string;
  referrerIds?: string[];
  serviceItems?: any[];
  onChange?: (data: DealCommissionOverrideFormData) => void;
  readonly?: boolean;
  previewMode?: boolean;
  // Props per la fatturazione
  singleInvoiceMode?: boolean;
  singleInvoiceDate?: Date | null;
  onSingleInvoiceDateChange?: (date: Date | null) => void;
  language?: string;
  // Props per la decurtazione automatica
  overrideCommissionScaling?: boolean | null;
  onOverrideCommissionScalingChange?: (value: boolean | null) => void;
  // Props per condividere lo stato "user edited" tra istanze diverse
  hasUserEditedCommission?: boolean;
  onUserEdit?: () => void;
  // Dati commissione iniziali dal parent (per mantenere i valori al cambio step)
  initialCommissionData?: DealCommissionOverrideFormData;
}

export function DealCommissionSection({
  dealId,
  clientId,
  assignedTo,
  referrerIds = [],
  serviceItems = [],
  onChange,
  readonly = false,
  previewMode = false,
  singleInvoiceMode = false,
  singleInvoiceDate,
  onSingleInvoiceDateChange,
  language = 'it',
  overrideCommissionScaling,
  onOverrideCommissionScalingChange,
  hasUserEditedCommission = false,
  onUserEdit,
  initialCommissionData
}: DealCommissionSectionProps) {
  const {
    dealOverrides,
    loading,
    fetchDealCommissionOverrides,
    getCommissionHierarchy
  } = useDealCommissionStore();

  const { referrers, fetchReferrers } = useReferrerStore();
  const { users, fetchUsers } = useUserStore();
  const { getEffectiveUserRate, getEffectiveReferrerRate } = useServiceCommissionStore();

  const [formData, setFormData] = useState<DealCommissionOverrideFormData>(() => {
    // Se ci sono dati iniziali dal parent (es. da un'altra istanza), usali
    if (initialCommissionData && (
      initialCommissionData.user_commission_rate !== undefined ||
      (initialCommissionData.referrer_commission_overrides && initialCommissionData.referrer_commission_overrides.length > 0)
    )) {
      return initialCommissionData;
    }
    return {
      user_commission_type: 'percentage',
      referrer_commission_overrides: [],
      override_reason: ''
    };
  });

  const [userHierarchy, setUserHierarchy] = useState<CommissionHierarchyDisplay | null>(null);
  const [referrerHierarchies, setReferrerHierarchies] = useState<Record<string, CommissionHierarchyDisplay>>({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showHierarchyInfo, setShowHierarchyInfo] = useState(false);
  const [userServiceRates, setUserServiceRates] = useState<Record<string, number>>({});
  const [userGeneralRate, setUserGeneralRate] = useState<number | null>(null);
  const [referrerServiceRates, setReferrerServiceRates] = useState<Record<string, Record<string, number>>>({});

  // Stato per commissioni bloccate PER-PERSONA (con pagamenti già effettuati)
  const [isAgentCommissionLocked, setIsAgentCommissionLocked] = useState(false);
  const [lockedReferrerIds, setLockedReferrerIds] = useState<Set<string>>(new Set());

  // Ref to track if commissions have been loaded for current dealId (prevents race conditions)
  const hasLoadedCommissionsRef = useRef<string | null>(null);

  // Ref to track if user has manually edited commission values (prevents useEffect from overwriting)
  const hasUserEditedRef = useRef(false);

  // Ref to track if user has manually edited referrer commission values
  const hasUserEditedReferrerRef = useRef(false);

  // Ref to store onChange to avoid re-runs when parent re-renders
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // FIX: Sincronizza con dati parent al mount quando l'utente ha già editato
  // IMPORTANTE: Usare refs per evitare che il sync si attivi quando initialCommissionData cambia
  // come risultato di una modifica dell'utente (causerebbe sovrascrittura con valore vecchio)
  const didSyncOnMount = useRef(false);
  const initialCommissionDataRef = useRef(initialCommissionData);
  const hasUserEditedCommissionRef = useRef(hasUserEditedCommission);

  // Aggiorna i refs solo quando cambiano (per accesso nell'useEffect)
  initialCommissionDataRef.current = initialCommissionData;
  hasUserEditedCommissionRef.current = hasUserEditedCommission;

  // Sync SOLO al primo mount (dependencies vuote)
  useEffect(() => {
    const currentInitialData = initialCommissionDataRef.current;
    const currentHasUserEdited = hasUserEditedCommissionRef.current;

    if (!didSyncOnMount.current && currentHasUserEdited && currentInitialData) {
      const hasData =
        (currentInitialData.referrer_commission_overrides && currentInitialData.referrer_commission_overrides.length > 0) ||
        currentInitialData.user_commission_rate !== undefined;

      if (hasData) {
        didSyncOnMount.current = true;
        setFormData(currentInitialData);
      }
    }
  }, []); // Dependencies vuote = esegue SOLO al mount

  // Load data on mount
  useEffect(() => {
    if (dealId) {
      // Reset user edit flags when loading a different deal
      hasUserEditedRef.current = false;
      hasUserEditedReferrerRef.current = false;
      fetchDealCommissionOverrides(dealId);
    } else {
      // Reset everything when creating a new deal (no dealId)
      // This prevents carrying over commission data from previously edited deals
      // BUT: Don't reset if user has edited values OR parent already has data
      // IMPORTANTE: hasUserEditedCommission viene dal parent e persiste tra remount del componente
      const hasUserEdits = hasUserEditedRef.current || hasUserEditedReferrerRef.current || hasUserEditedCommission;
      const hasParentData =
        (initialCommissionData?.referrer_commission_overrides && initialCommissionData.referrer_commission_overrides.length > 0) ||
        initialCommissionData?.user_commission_rate !== undefined;

      if (!hasUserEdits && !hasParentData) {
        // 1. Reset store
        useDealCommissionStore.setState({ dealOverrides: [] });

        // 2. Reset local formData
        const cleanFormData = {
          user_commission_type: 'percentage' as const,
          referrer_commission_overrides: [],
          override_reason: ''
        };
        setFormData(cleanFormData);

        // Reset refs so new deal can load fresh
        hasLoadedCommissionsRef.current = null;
        hasUserEditedRef.current = false;
        hasUserEditedReferrerRef.current = false;

        // 3. Notify parent to clear its state too
        onChangeRef.current?.(cleanFormData);
      } else if (hasParentData && !hasUserEdits) {
        // Parent has data but user hasn't edited - use parent data to initialize local state
        setFormData(initialCommissionData);
      }
      // If hasUserEdits is true, keep current formData (user's edits)
    }
    fetchReferrers();
  }, [dealId, clientId]);

  // Verifica se esistono pagamenti PER-PERSONA per le commissioni di questo deal
  useEffect(() => {
    const checkPayments = async () => {
      if (!dealId) {
        setIsAgentCommissionLocked(false);
        setLockedReferrerIds(new Set());
        return;
      }

      try {
        // Recupera organization_id e assigned_to dal deal
        const { data: deal } = await supabase
          .from('deals')
          .select('organization_id, assigned_to')
          .eq('id', dealId)
          .single();

        if (!deal?.organization_id) return;

        // Verifica pagamenti AGENTE
        if (deal.assigned_to) {
          const agentLocked = await hasPersonCommissionPayments(
            dealId, deal.assigned_to, 'agent', deal.organization_id
          );
          setIsAgentCommissionLocked(agentLocked);
          if (agentLocked) {
            console.log('🔒 [DealCommissionSection] Agent commission locked');
          }
        }

        // Verifica pagamenti per ogni SEGNALATORE
        const lockedRefs = new Set<string>();
        for (const refId of referrerIds) {
          const refLocked = await hasPersonCommissionPayments(
            dealId, refId, 'referrer', deal.organization_id
          );
          if (refLocked) {
            lockedRefs.add(refId);
            console.log(`🔒 [DealCommissionSection] Referrer ${refId} commission locked`);
          }
        }
        setLockedReferrerIds(lockedRefs);
      } catch (error) {
        console.error('Error checking commission payments:', error);
      }
    };

    checkPayments();
  }, [dealId, referrerIds]);

  // Load existing referrer commissions from deal_referrers when editing a deal
  useEffect(() => {
    const loadExistingCommissions = async () => {
      if (!dealId || referrerIds.length === 0) return;

      // Skip if already loaded for this dealId (prevents race conditions)
      if (hasLoadedCommissionsRef.current === dealId) return;

      // FIX: Skip if user has already edited commissions (prevents overwriting user edits)
      if (hasUserEditedCommission) return;

      hasLoadedCommissionsRef.current = dealId;

      const { fetchDealReferrers } = useReferrerStore.getState();
      const dealReferrers = await fetchDealReferrers(dealId);

      const referrerOverrides = dealReferrers
        .filter(r => r.commission_rate !== null || r.commission_fixed_amount !== null)
        .map(r => ({
          referrer_id: r.referrer_id,
          // Use fixed_amount if commission_type is 'fixed', otherwise use commission_rate
          commission_rate: r.commission_type === 'fixed' ? r.commission_fixed_amount : r.commission_rate,
          commission_type: (r.commission_type || 'percentage') as 'percentage' | 'fixed',
          source: 'custom' as const
        }));

      if (referrerOverrides.length > 0) {
        setFormData(prev => {
          const newFormData = {
            ...prev,
            referrer_commission_overrides: referrerOverrides
          };
          // Also notify parent so it has the loaded data
          onChangeRef.current?.(newFormData);
          return newFormData;
        });
      }
    };

    loadExistingCommissions();
  }, [dealId, referrerIds.length]);

  // Load commission hierarchies — sempre via resolve_commission_rate (la stessa gerarchia del
  // motore V2), anche in preview/creazione dove dealId non esiste ancora (P12).
  useEffect(() => {
    const loadHierarchies = async () => {
      // Load user commission hierarchy
      if (assignedTo) {
        const hierarchy = await getCommissionHierarchy(dealId || '', assignedTo, undefined, 'user');
        setUserHierarchy(hierarchy);
      } else {
        setUserHierarchy(null);
      }

      // Load referrer commission hierarchies
      const refHierarchies: Record<string, CommissionHierarchyDisplay> = {};
      for (const referrerId of referrerIds) {
        const hierarchy = await getCommissionHierarchy(dealId || '', undefined, referrerId, 'referrer');
        if (hierarchy) {
          refHierarchies[referrerId] = hierarchy;
        }
      }
      setReferrerHierarchies(refHierarchies);
    };

    loadHierarchies();
  }, [dealId, assignedTo, JSON.stringify(referrerIds), previewMode, clientId]);

  // Load users and service-specific commission rates for assigned user
  useEffect(() => {
    // Load users if not already loaded
    fetchUsers();

    // Load general user commission rate via resolve_commission_rate (senza servizio = rate
    // default utente/gruppo/org — la stessa gerarchia del motore V2)
    const loadGeneralRate = async () => {
      if (!assignedTo) {
        setUserGeneralRate(null);
        return;
      }

      try {
        // Get organization ID
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) return;

        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('organization_id')
          .eq('id', userData.user.id)
          .single();

        if (!userProfile?.organization_id) return;

        const { data: resolved } = await supabase.rpc('resolve_commission_rate', {
          p_organization_id: userProfile.organization_id,
          p_deal_id: null,
          p_service_id: null,
          p_person_id: assignedTo,
          p_person_type: 'user'
        });

        setUserGeneralRate(resolved?.[0]?.rate ?? 0);
      } catch (error) {
        console.error('Error loading general user rate:', error);
        setUserGeneralRate(0);
      }
    };

    // Load service-specific commission rates for the assigned user
    const loadServiceRates = async () => {
      if (!assignedTo || serviceItems.length === 0) {
        setUserServiceRates({});
        return;
      }

      console.log('[DealCommissionSection] Loading service rates for user:', assignedTo);

      const rates: Record<string, number> = {};
      for (const service of serviceItems) {
        const serviceId = service.service_id;
        if (serviceId) {
          try {
            const rate = await getEffectiveUserRate(serviceId, assignedTo);
            console.log('[DealCommissionSection] Got rate for service:', { serviceId, rate });
            rates[serviceId] = rate;
          } catch (error) {
            console.error(`Error loading rate for service ${serviceId}:`, error);
          }
        }
      }
      setUserServiceRates(rates);
    };

    loadGeneralRate();
    loadServiceRates();
  }, [assignedTo, JSON.stringify(serviceItems.map(s => s.service_id))]);

  // Load service-specific commission rates for each referrer
  useEffect(() => {
    const loadReferrerServiceRates = async () => {
      if (referrerIds.length === 0 || serviceItems.length === 0) {
        setReferrerServiceRates({});
        return;
      }

      const rates: Record<string, Record<string, number>> = {};
      for (const referrerId of referrerIds) {
        rates[referrerId] = {};
        for (const service of serviceItems) {
          const serviceId = service.service_id;
          if (serviceId) {
            try {
              const rate = await getEffectiveReferrerRate(serviceId, referrerId);
              rates[referrerId][serviceId] = rate;
            } catch (error) {
              console.error(`Error loading referrer rate for service ${serviceId}:`, error);
            }
          }
        }
      }
      setReferrerServiceRates(rates);
    };

    loadReferrerServiceRates();
  }, [JSON.stringify(referrerIds), JSON.stringify(serviceItems.map(s => s.service_id))]);

  // Update form data when overrides change
  useEffect(() => {
    // If user has manually edited values (in this or another instance), don't overwrite with store data
    if (hasUserEditedRef.current || hasUserEditedReferrerRef.current || hasUserEditedCommission) return;

    // FIX: Cercare l'override utente per tipo di record (referrer_id null = utente),
    // non per valore del rate (che potrebbe essere null per commissioni fisse)
    const userOverride = dealOverrides.find(o =>
      o.referrer_id === null && o.user_commission_source === 'custom'
    );
    const referrerOverridesFromStore = dealOverrides.filter(o => o.referrer_id);

    // Build new formData, but preserve referrer_commission_overrides if already loaded from deal_referrers
    // This prevents the race condition where dealOverrides (empty) overwrites data loaded from deal_referrers
    const newReferrerOverrides = referrerOverridesFromStore.length > 0
      ? referrerOverridesFromStore.map(o => ({
          referrer_id: o.referrer_id!,
          commission_rate: o.referrer_commission_rate,
          commission_type: o.referrer_commission_type,
          source: o.referrer_commission_source
        }))
      : undefined; // Don't overwrite if store is empty

    setFormData(prev => ({
      user_commission_rate: userOverride?.user_commission_rate,
      user_commission_type: userOverride?.user_commission_type || 'percentage',
      // Only overwrite referrer_commission_overrides if we got data from store
      // Otherwise keep what was loaded from deal_referrers
      referrer_commission_overrides: newReferrerOverrides ?? prev.referrer_commission_overrides,
      override_reason: userOverride?.override_reason || ''
    }));

    // Notify parent component of loaded commission data
    // This ensures the parent has the current values when editing an existing deal
    // IMPORTANT: Only propagate commission data when editing an existing deal (dealId is present)
    // When creating a new deal (no dealId), we should NOT propagate any commission data
    // FIX 2026-03-13: Usa onChangeRef.current invece di onChange per evitare loop infinito
    if (onChangeRef.current && dealOverrides.length > 0 && dealId) {
      const newFormData = {
        user_commission_rate: userOverride?.user_commission_rate,
        user_commission_type: userOverride?.user_commission_type || 'percentage',
        referrer_commission_overrides: referrerOverridesFromStore.map(o => ({
          referrer_id: o.referrer_id!,
          commission_rate: o.referrer_commission_rate,
          commission_type: o.referrer_commission_type,
          source: o.referrer_commission_source
        })),
        override_reason: userOverride?.override_reason || ''
      };
      onChangeRef.current(newFormData);
    }
  }, [dealOverrides, dealId, hasUserEditedCommission]); // FIX: rimosso onChange dalle dipendenze

  const handleReferrerCommissionChange = (referrerId: string, rate: number | undefined, commissionType?: 'percentage' | 'fixed') => {
    // Mark that user has manually edited referrer commission, so useEffect won't reset
    hasUserEditedReferrerRef.current = true;
    // Notify parent that user has edited (shared between all instances)
    onUserEdit?.();

    const existingOverride = formData.referrer_commission_overrides.find(o => o.referrer_id === referrerId);

    const newOverrides = formData.referrer_commission_overrides.map(o =>
      o.referrer_id === referrerId
        ? {
            ...o,
            commission_rate: rate,
            commission_type: commissionType ?? o.commission_type,
            source: rate !== undefined ? 'custom' as const : 'client_default' as const
          }
        : o
    );

    // Add new referrer if not exists
    if (!existingOverride) {
      newOverrides.push({
        referrer_id: referrerId,
        commission_rate: rate,
        commission_type: commissionType ?? 'percentage',
        source: 'custom'
      });
    }

    const newFormData = {
      ...formData,
      referrer_commission_overrides: newOverrides
    };
    console.log('🔄 [DealCommissionSection] handleReferrerCommissionChange:', {
      referrerId,
      rate,
      commissionType,
      newOverrides,
      newFormData
    });
    setFormData(newFormData);
    onChange?.(newFormData);
  };

  const handleUserCommissionChange = (rate: number | undefined, commissionType?: 'percentage' | 'fixed') => {
    // Mark that user has manually edited, so useEffect won't overwrite
    hasUserEditedRef.current = true;
    // Notify parent that user has edited (shared between all instances)
    onUserEdit?.();

    const newFormData = {
      ...formData,
      user_commission_rate: rate,
      user_commission_type: commissionType ?? formData.user_commission_type
    };
    setFormData(newFormData);
    onChange?.(newFormData);
  };

  const getSourceLabel = (source: string) => {
    switch (source) {
      case 'deal_override': return 'Personalizzata per questa offerta';
      case 'client_default': return 'Default del cliente';
      case 'service_default': return 'Default del servizio';
      case 'user_default': return 'Default dell\'utente';
      case 'system_default': return 'Default del sistema';
      default: return source;
    }
  };

  // Per un segnalatore la base viene dal gruppo segnalatori o dalla sua scheda, non da un utente
  const getReferrerSourceLabel = (source: string) =>
    source === 'user_default' ? 'Default del segnalatore' : getSourceLabel(source);

  const getSourceColor = (source: string) => {
    switch (source) {
      case 'deal_override': return 'text-blue-600 bg-blue-50';
      case 'client_default': return 'text-green-600 bg-green-50';
      case 'service_default': return 'text-purple-600 bg-purple-50';
      case 'user_default': return 'text-orange-600 bg-orange-50';
      case 'system_default': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          
          <h3 className="text-lg font-medium text-black dark:text-white">
            {previewMode ? 'Anteprima commissioni' : 'Commissioni per questa offerta'}
          </h3>
          {previewMode && (
            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
              Anteprima
            </span>
          )}
          <button
            onClick={() => setShowHierarchyInfo(!showHierarchyInfo)}
            className="p-1 hover:bg-black/10 dark:hover:bg-white/10 rounded-full transition-colors"
            title="Info gerarchia commissioni"
          >
            <Info className="h-4 w-4 text-black dark:text-white" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-sm px-3 py-1 bg-white hover:bg-gray-200 dark:bg-white dark:hover:bg-gray-200 text-black rounded-lg transition-colors"
          >
            {showAdvanced ? 'Semplice' : 'Avanzato'}
          </button>
        </div>
      </div>

      {/* No data message for preview mode */}
      {previewMode && (!clientId || !assignedTo) && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium mb-1">Dati insufficienti per calcolare le commissioni</p>
              <p>Seleziona un cliente e assegna l'offerta per vedere le commissioni che verranno applicate automaticamente.</p>
            </div>
          </div>
        </div>
      )}

      {/* User Commission Section - Stile Uniforme */}
      {assignedTo && (
        <div className="mb-6">
          {/* Header sezione */}
          <div className="flex items-center gap-2 mb-3">
            <h4 className="text-sm font-medium text-black dark:text-white">
              Commissione utente assegnato
            </h4>
            {userHierarchy && (
              <span className="text-[11px] px-2 py-0.5 rounded bg-black/5 dark:bg-white/10 text-black/60 dark:text-white/60">
                {getSourceLabel(userHierarchy.current_source)}
              </span>
            )}
          </div>

          {/* Info agente - SENZA CARD, solo riga */}
          <div className="flex items-center justify-between py-2 border-b border-black/10 dark:border-white/10 mb-4">
            <span className="text-sm font-medium text-black dark:text-white">
              {users.find(u => u.id === assignedTo)?.full_name || 'Utente'}
            </span>
            <span className="text-sm text-black/70 dark:text-white/70">
              {(() => {
                const rate = userHierarchy?.current_rate ?? userGeneralRate;
                return rate !== null && rate !== undefined ? `${rate}%` : '—';
              })()}
              <span className="text-[11px] ml-1.5 text-black/50 dark:text-white/50">
                ({getSourceLabel(userHierarchy?.current_source || 'system_default')})
              </span>
            </span>
          </div>

          {/* Lista commissioni per servizio - SOLO se almeno un servizio ha una commissione specifica diversa */}
          {serviceItems.length > 0 && Object.keys(userServiceRates).length > 0 &&
            serviceItems.some(service => {
              const serviceRate = userServiceRates[service.service_id];
              const generalRate = userHierarchy?.current_rate ?? userGeneralRate;
              return serviceRate !== undefined && generalRate !== undefined && serviceRate !== generalRate;
            }) && (
            <div className="mb-4">
              <p className="text-[11px] uppercase tracking-wider font-medium text-black/50 dark:text-white/50 mb-2">
                Commissioni per servizio
              </p>
              <div className="space-y-0">
                {serviceItems.map((service, index) => {
                  const serviceId = service.service_id;
                  const serviceName = service.service?.title || service.custom_service_name || 'Servizio';
                  const serviceRate = userServiceRates[serviceId];
                  const generalRate = userHierarchy?.current_rate ?? userGeneralRate;
                  const isDifferent = serviceRate !== undefined && generalRate !== undefined && serviceRate !== generalRate;

                  return (
                    <div
                      key={serviceId || service.id}
                      className={`flex items-center justify-between py-2
                        ${index < serviceItems.length - 1 ? 'border-b border-black/5 dark:border-white/5' : ''}`}
                    >
                      <span className="text-sm text-black dark:text-white">{serviceName}</span>
                      <span className={`text-sm font-medium ${
                        isDifferent
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-black/60 dark:text-white/60'
                      }`}>
                        {serviceRate !== undefined ? `${serviceRate}%` : `${generalRate || 0}%`}
                        {isDifferent && (
                          <span className="text-[10px] ml-1.5 px-1.5 py-0.5 rounded bg-blue-500/10 dark:bg-blue-500/20">
                            specifica
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Override commissione utente */}
          {!readonly && !previewMode && (
            <div className="mt-4 pt-4 border-t border-black/10 dark:border-white/10">
              <p className="text-[11px] uppercase tracking-wider font-medium text-black/50 dark:text-white/50 mb-2">
                Sovrascrivi commissione
              </p>

              {/* Messaggio di blocco se AGENTE ha pagamenti */}
              {isAgentCommissionLocked ? (
                <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <Lock className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    Commissione agente con pagamenti registrati - modifica non consentita
                  </p>
                </div>
              ) : (
              <>
              <div className="flex items-center gap-2">
                {/* Toggle % / € */}
                <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => handleUserCommissionChange(formData.user_commission_rate, 'percentage')}
                    className={`px-3 py-2 text-sm font-medium transition-colors ${
                      formData.user_commission_type === 'percentage'
                        ? 'bg-amber-500 text-white'
                        : 'bg-white dark:bg-gray-800 text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    %
                  </button>
                  <button
                    type="button"
                    onClick={() => handleUserCommissionChange(formData.user_commission_rate, 'fixed')}
                    className={`px-3 py-2 text-sm font-medium transition-colors ${
                      formData.user_commission_type === 'fixed'
                        ? 'bg-amber-500 text-white'
                        : 'bg-white dark:bg-gray-800 text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    €
                  </button>
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={formData.user_commission_type === 'percentage' ? 100 : undefined}
                  value={formData.user_commission_rate ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    handleUserCommissionChange(val === '' ? undefined : parseFloat(val));
                  }}
                  className="flex-1 px-3 py-2 glass-card rounded-lg text-black dark:text-white placeholder:text-white dark:placeholder:text-white/70"
                  placeholder={
                    formData.user_commission_type === 'percentage'
                      ? `Default: ${userHierarchy?.current_rate ?? userGeneralRate ?? 10}%`
                      : "Importo fisso"
                  }
                />
                <span className="text-sm text-black dark:text-white min-w-[20px]">
                  {formData.user_commission_type === 'percentage' ? '%' : '€'}
                </span>
              </div>
              {formData.user_commission_rate === undefined && formData.user_commission_type === 'percentage' && userHierarchy && (
                <p className="text-sm text-black/60 dark:text-white/60 mt-1">
                  Usando {userHierarchy.current_rate}% ({getSourceLabel(userHierarchy.current_source)})
                </p>
              )}
              {formData.user_commission_type === 'fixed' && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                  La commissione fissa non viene decurtata dai segnalatori
                </p>
              )}
              </>
              )}
            </div>
          )}

        </div>
      )}

      {/* Referrer Commissions Section */}
      {referrerIds.length > 0 && (
        <div className="mb-6">
          <h4 className="text-md font-medium text-black dark:text-white mb-3">Commissioni segnalatori</h4>
          <div className="space-y-4">
            {referrerIds.map(referrerId => {
              const referrer = referrers.find(r => r.id === referrerId);
              const hierarchy = referrerHierarchies[referrerId];
              const currentOverride = formData.referrer_commission_overrides.find(o => o.referrer_id === referrerId);
              // Stessa precedenza del motore provvigioni: una percentuale scritta sull'offerta
              // vale per tutti i servizi; altrimenti ogni servizio usa la sua (gruppo
              // segnalatori o segnalatore), e le righe senza servizio di catalogo la base.
              // Prima l'intestazione mostrava la percentuale personale del segnalatore e il
              // campo "Usando 20%" anche quando il calcolo applicava il 10% del gruppo su un
              // servizio (segnalazione FinUp, HUAMEI).
              const generalRate = hierarchy?.current_rate ?? referrer?.commission_rate ?? 0;
              const manualRate = (currentOverride?.commission_type ?? 'percentage') === 'percentage'
                ? currentOverride?.commission_rate
                : undefined;
              const rateForService = (serviceId?: string | null) =>
                manualRate ?? (serviceId ? referrerServiceRates[referrerId]?.[serviceId] : undefined) ?? generalRate;
              // Con una base a importo fisso il motore applica solo quella, una volta per offerta
              const hasServiceSpecificRates = manualRate === undefined &&
                currentOverride?.commission_type !== 'fixed' &&
                hierarchy?.current_type !== 'fixed' &&
                serviceItems.some(service => rateForService(service.service_id) !== generalRate);

              return (
                <div key={referrerId} className="p-4 glass-card rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-black dark:text-white">
                        {formatReferrerName(referrer?.full_name) || 'Segnalatore'}
                        {currentOverride?.commission_type !== 'fixed' && (
                          <span className="text-black/60 dark:text-white/60 font-normal">
                            {' '}({hasServiceSpecificRates
                              ? 'percentuale per servizio'
                              : manualRate === undefined && hierarchy?.current_type === 'fixed'
                                ? `€${generalRate}`
                                : `${manualRate ?? generalRate}%`})
                          </span>
                        )}
                      </span>
                      {hasServiceSpecificRates ? (
                        <span className={`text-xs px-2 py-1 rounded-full ${getSourceColor('service_default')}`}>
                          Per servizio
                        </span>
                      ) : hierarchy && (
                        <span className={`text-xs px-2 py-1 rounded-full ${getSourceColor(hierarchy.current_source)}`}>
                          {getReferrerSourceLabel(hierarchy.current_source)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Lista commissioni per servizio - Segnalatore - SOLO se almeno un servizio ha una commissione specifica diversa */}
                  {hasServiceSpecificRates && (
                    <div className="mb-4">
                      <p className="text-[11px] uppercase tracking-wider font-medium text-black/50 dark:text-white/50 mb-2">
                        Commissioni per servizio
                      </p>
                      <div className="space-y-0">
                        {serviceItems.map((service, index) => {
                          const serviceId = service.service_id;
                          const serviceName = service.service?.title || service.custom_service_name || 'Servizio';
                          const serviceRate = rateForService(serviceId);
                          const isDifferent = serviceRate !== generalRate;

                          return (
                            <div
                              key={serviceId || service.id}
                              className={`flex items-center justify-between py-2
                                ${index < serviceItems.length - 1 ? 'border-b border-black/5 dark:border-white/5' : ''}`}
                            >
                              <span className="text-sm text-black dark:text-white">{serviceName}</span>
                              <span className={`text-sm font-medium ${
                                isDifferent
                                  ? 'text-blue-600 dark:text-blue-400'
                                  : 'text-black/60 dark:text-white/60'
                              }`}>
                                {`${serviceRate}%`}
                                {isDifferent && (
                                  <span className="text-[10px] ml-1.5 px-1.5 py-0.5 rounded bg-blue-500/10 dark:bg-blue-500/20">
                                    specifica
                                  </span>
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Controllo blocco per-persona per questo segnalatore */}
                  {lockedReferrerIds.has(referrerId) ? (
                    <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                      <Lock className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                      <p className="text-sm text-amber-800 dark:text-amber-200">
                        Commissione segnalatore con pagamenti registrati - modifica non consentita
                      </p>
                    </div>
                  ) : (
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        {/* Toggle per tipo commissione referrer */}
                        <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                          <button
                            type="button"
                            onClick={() => handleReferrerCommissionChange(referrerId, currentOverride?.commission_rate, 'percentage')}
                            disabled={readonly || previewMode}
                            className={`px-3 py-2 text-sm font-medium transition-colors ${
                              (currentOverride?.commission_type ?? 'percentage') === 'percentage'
                                ? 'bg-amber-500 text-white'
                                : 'bg-white dark:bg-gray-800 text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                            } disabled:opacity-50`}
                          >
                            %
                          </button>
                          <button
                            type="button"
                            onClick={() => handleReferrerCommissionChange(referrerId, currentOverride?.commission_rate, 'fixed')}
                            disabled={readonly || previewMode}
                            className={`px-3 py-2 text-sm font-medium transition-colors ${
                              currentOverride?.commission_type === 'fixed'
                                ? 'bg-amber-500 text-white'
                                : 'bg-white dark:bg-gray-800 text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                            } disabled:opacity-50`}
                          >
                            €
                          </button>
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max={(currentOverride?.commission_type ?? 'percentage') === 'percentage' ? 100 : undefined}
                          value={currentOverride?.commission_rate ?? ''}
                          onChange={(e) => handleReferrerCommissionChange(referrerId, e.target.value ? parseFloat(e.target.value) : undefined)}
                          disabled={readonly || previewMode}
                          className="flex-1 px-3 py-2 glass-card rounded-lg text-black dark:text-white placeholder:text-white dark:placeholder:text-white/70 disabled:opacity-50"
                          placeholder={
                            (currentOverride?.commission_type ?? 'percentage') === 'percentage'
                              ? (hasServiceSpecificRates
                                  ? 'Per servizio (vedi sopra)'
                                  : (hierarchy ? `Default: ${hierarchy.current_rate}%` : "0.00"))
                              : "Importo fisso"
                          }
                        />
                        <span className="text-sm text-black dark:text-white min-w-[20px]">
                          {(currentOverride?.commission_type ?? 'percentage') === 'percentage' ? '%' : '€'}
                        </span>
                      </div>
                      {hasServiceSpecificRates ? (
                        <p className="text-sm text-black/60 dark:text-white/60 mt-1">
                          Usando le percentuali per servizio indicate sopra. Un valore scritto qui vale per tutti i servizi dell'offerta.
                        </p>
                      ) : hierarchy && currentOverride?.commission_rate === undefined && (currentOverride?.commission_type ?? 'percentage') === 'percentage' && (
                        <p className="text-sm text-black/60 dark:text-white/60 mt-1">
                          Usando {hierarchy.current_rate}% ({getReferrerSourceLabel(hierarchy.current_source)})
                        </p>
                      )}
                      {currentOverride?.commission_type === 'fixed' && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                          La commissione fissa non decurta la provvigione dell'agente
                        </p>
                      )}
                    </div>
                  </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Checkbox per disabilitare decurtazione provvigioni */}
          {onOverrideCommissionScalingChange && (
            <div className="flex items-start gap-3 pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
              <input
                type="checkbox"
                id="overrideCommissionScaling"
                checked={overrideCommissionScaling === false}
                onChange={(e) => onOverrideCommissionScalingChange(e.target.checked ? false : null)}
                disabled={readonly || previewMode}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              <div>
                <label htmlFor="overrideCommissionScaling" className="font-medium text-black dark:text-white cursor-pointer">
                  {language === 'it' ? 'Disabilita decurtazione automatica' : 'Disable automatic deduction'}
                </label>
                <p className="text-sm text-black/60 dark:text-white/60 mt-1">
                  {language === 'it'
                    ? 'Se attivo, la commissione dell\'agente non verrà decurtata della retrocessione del segnalatore per tutti i servizi di questa offerta.'
                    : 'If enabled, the agent\'s commission will not be reduced by the referrer\'s retrocession for all services in this offer.'}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Debug Info */}
      {previewMode && process.env.NODE_ENV === 'development' && (
        <div className="mb-4 p-3 bg-gray-100 border rounded text-xs">
          <p>Debug: previewMode={previewMode.toString()}, serviceItems.length={serviceItems.length}</p>
          <p>userHierarchy: {userHierarchy ? `${userHierarchy.current_rate}% (${userHierarchy.current_source})` : 'null'}</p>
          <p>referrerHierarchies: {JSON.stringify(Object.keys(referrerHierarchies))}</p>
          <p>clientId: {clientId}, assignedTo: {assignedTo}</p>
        </div>
      )}

      {/* Commission Calculation Summary */}
      {previewMode && serviceItems.length > 0 && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="text-md font-medium text-green-800 mb-3">Calcolo Commissioni Totali</h4>
          <div className="space-y-2">
            {serviceItems.map((service, index) => {
              const serviceAmount = service.discounted_amount || service.amount || 0;
              const serviceName = service.service?.title || service.custom_service_name || `Servizio ${index + 1}`;
              
              return (
                <div key={index} className="flex items-center justify-between text-sm">
                  <span className="text-green-700">{serviceName}</span>
                  <span className="text-green-800 font-medium">€{serviceAmount.toFixed(2)}</span>
                </div>
              );
            })}
            <div className="border-t border-green-200 pt-2 mt-2">
              <div className="flex items-center justify-between text-sm font-medium">
                <span className="text-green-800">Totale Servizi</span>
                <span className="text-green-900">€{serviceItems.reduce((total, service) => total + (service.discounted_amount || service.amount || 0), 0).toFixed(2)}</span>
              </div>
              {userHierarchy ? (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-green-700">Commissione Utente ({userHierarchy.current_rate}%)</span>
                  <span className="text-green-800">€{(serviceItems.reduce((total, service) => total + (service.discounted_amount || service.amount || 0), 0) * (userHierarchy.current_rate / 100)).toFixed(2)}</span>
                </div>
              ) : assignedTo && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-green-700">Commissione Utente (10% sistema)</span>
                  <span className="text-green-800">€{(serviceItems.reduce((total, service) => total + (service.discounted_amount || service.amount || 0), 0) * 0.10).toFixed(2)}</span>
                </div>
              )}
              {Object.entries(referrerHierarchies).map(([referrerId, hierarchy]) => {
                const referrer = referrers.find(r => r.id === referrerId);
                const totalAmount = serviceItems.reduce((total, service) => total + (service.discounted_amount || service.amount || 0), 0);
                const commissionAmount = totalAmount * (hierarchy.current_rate / 100);
                
                return (
                  <div key={referrerId} className="flex items-center justify-between text-sm">
                    <span className="text-green-700">Commissione {formatReferrerName(referrer?.full_name) || 'Segnalatore'} ({hierarchy.current_rate}%)</span>
                    <span className="text-green-800">€{commissionAmount.toFixed(2)}</span>
                  </div>
                );
              })}
              {referrerIds.length > 0 && Object.keys(referrerHierarchies).length === 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-green-700">Commissione Segnalatori (5% sistema)</span>
                  <span className="text-green-800">€{(serviceItems.reduce((total, service) => total + (service.discounted_amount || service.amount || 0), 0) * 0.05).toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Override Reason */}
      {showAdvanced && !previewMode && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-black dark:text-white mb-2">
            Motivo Personalizzazione (opzionale)
          </label>
          <textarea
            value={formData.override_reason}
            onChange={(e) => setFormData({ ...formData, override_reason: e.target.value })}
            disabled={readonly}
            rows={2}
            className="w-full px-3 py-2 glass-card rounded-lg text-black dark:text-white disabled:opacity-50"
            placeholder="Es: Cliente premium, condizioni speciali accordate..."
          />
        </div>
      )}

      {/* Data emissione fattura (visibile solo se singleInvoiceMode attivo) */}
      {singleInvoiceMode && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <label className="block text-sm font-medium text-black dark:text-white mb-2">
            {language === 'it' ? 'Data emissione fattura' : 'Invoice issue date'}
          </label>
          <DatePicker
            selected={singleInvoiceDate}
            onChange={(date: Date | null) => onSingleInvoiceDateChange?.(date)}
            dateFormat="dd/MM/yyyy"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholderText={language === 'it' ? 'Seleziona data emissione' : 'Select issue date'}
            isClearable
          />
          <p className="text-xs text-black/50 dark:text-white/50 mt-2">
            {language === 'it'
              ? 'La fattura verrà emessa in questa data con tutte le righe e le scadenze di pagamento configurate.'
              : 'The invoice will be issued on this date with all lines and configured payment due dates.'}
          </p>
        </div>
      )}

      {/* Info Box - Conditional */}
      {showHierarchyInfo && (
        <div className="glass-card rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Info className="h-5 w-5 text-black dark:text-white mt-0.5 flex-shrink-0" />
            <div className="text-sm text-black dark:text-white">
              <p className="font-medium mb-1">Gerarchia delle Commissioni:</p>
              <ol className="list-decimal list-inside space-y-1 text-black/80 dark:text-white/80">
                <li>Percentuali personalizzate per questa offerta (priorità massima)</li>
                <li>Percentuali per servizio (utente o segnalatore)</li>
                <li>Percentuali del gruppo provvigionale</li>
                <li>Percentuali di default della persona</li>
                <li>Percentuale di default dell'organizzazione</li>
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}